import { RoomTypes } from '@weblite/ts-action/room'
import {
  CallType,
  CancelCall,
  JoinCall,
  RejectCall,
  reqCancelCall,
  reqJoinCall,
  reqRejectCall,
  reqStartCall,
  reqUpdateCall,
  StartCall,
  UpdateCall,
} from '@weblite/ts-action/media-proxy'
import cloneDeep from 'lodash.clonedeep'
import { eventCallNameChanged } from '@weblite-wapps/protocol'
import { showErrorSnackbar } from '$comp/snackbars'
import { showCallFeedbackDialog } from '$comp/modals/call-feedback'
import { getHall, isPublicHall } from '$logic/hall'
import { getRoom, getRooms } from '$logic/room'
import { CallWappPanel } from '$logic/wapp-panels'
import { getClientUserId } from '$logic/client-user'
import { getHallPushData, getPvPushData } from '$logic/message'
import { closePvCallManager, openCall, openPvCallManager } from '$logic/router'
import { sendEventToWapp } from '$logic/wapp-communication/wapp-communication.publish'
import { sendEventToTwa } from '$logic/twaCommunication/twaCommunication.api'
import { TWA_EVENTS, twaCommunication } from '$logic/twaCommunication'
import hostConf from '$config/frontend'
import { CallStorage, callStorage, CallStoragePvMeta } from './storage'
import {
  scheduleCall,
  unscheduleCall,
  updateScheduledCall,
} from '$logic/call/schedule.service'
import {
  getCallId,
  endOngoingCallWithConfirmation,
  isCallAtLeastOneMinuteLong,
  resetCallAndShowNPS,
} from './helpers'
import { request } from '$setup/ws'
import { t } from '$i18n'
import {
  createUserWaitReporter,
  UserWaitPendingAction,
} from '$logic/analytics/reporters/user-wait'
import { getClientUserIdentityToken } from '$logic/auth'
import { downloadFileFromUrl } from '$func/download'
import {
  CHECK_VPN_HOST,
  MEDIA_PROXY_API_URL,
  MEDIA_SERVER_API_URL,
} from '$config/env'
import {
  PremiumFeatureType,
  showBuyPremiumFeatureDialog,
} from '$comp/modals/buy-premium-feature-dialog'
import { showCallRoomIsFullDialog } from '$comp/modals/call-room-is-full'
import { DISABLE_WATERMARK_TAG } from '$logic/hall/constant'
import { ensureHallCallAccess } from './access'

const CALL_TYPE_TO_EVENT_MAPPER: Record<CallType, string> = {
  call: 'CHAT_START_CALL',
  group_call: 'CHAT_START_MEETING',
  live: 'CHAT_START_LIVE',
  classroom: 'CHAT_START_CLASSROOM',
  tribune: 'CHAT_START_TRIBUNE',
}

export const callServersConfig = {
  mediaProxyApiUrl: MEDIA_PROXY_API_URL,
  checkVPNHost: CHECK_VPN_HOST,
  recorderServerHost: 'http://localhost:3000',
  mediaServerApiUrl: MEDIA_SERVER_API_URL,
}

const startCallUserWaitReporter = createUserWaitReporter(
  UserWaitPendingAction.START_CALL,
)
async function startCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
  name?: string,
) {
  const {
    roomId: ongoingCallRoomId,
    pvMeta: { roomId: pvMetaRoomId, state: pvCallState },
  } = callStorage.get()

  if (ongoingCallRoomId) await endOngoingCallWithConfirmation(roomId, callType)
  analite(CALL_TYPE_TO_EVENT_MAPPER[callType])

  if (callType === 'call') {
    // ClientUser wants to call a user which already is in call with
    if (ongoingCallRoomId === roomId) return openCall()

    // ClientUser wants to call a user which already is calling him
    if (pvMetaRoomId === roomId && pvCallState === 'outgoing')
      return openPvCallManager()

    callStorage.reset()
    callStorage.set({
      pvMeta: {
        roomId,
        state: 'outgoing',
        isCaller: true,
      },
    })

    openPvCallManager()
  }

  startCallUserWaitReporter.start()
  const { error, payload } = await request<StartCall>(
    reqStartCall({
      roomId,
      roomType,
      callType,
      push: roomType === 'PV' ? getPvPushData() : await getHallPushData(roomId),
      options: {
        name,
      },
    }),
  )

  callStorage.set({ hasNextCall: false })

  if (error) {
    // TODO: Show error toast
    startCallUserWaitReporter.clear()
  } else if (callType !== 'call') {
    const { error: resError, token, contentUri, serverAddress } = payload
    if (resError) {
      startCallUserWaitReporter.clear()
      if (resError === 'USER_BANNED')
        showErrorSnackbar(t('snackbar.call.userBanned')(t(`call.${callType}`)))

      if (resError === 'PAYMENT_REQUIRED') {
        analite('OPEN_BUY_PREMIUM_PLAN_FEATURE_DIALOG', {
          roomId,
          source: 'HALL_APP_BAR_CALL_OPTION',
        })
        void showBuyPremiumFeatureDialog({
          featureType: PremiumFeatureType.CLASSROOM,
          hallId: roomId,
        })
        return
      }

      return
    }

    const { name: hallName, tags } = await getHall(roomId)
    const callName = name || hallName

    const sharedInput = {
      ...callServersConfig,
      title: callName,
      roomType,
      callType,
      mediaServerApiUrl: serverAddress || callServersConfig.mediaServerApiUrl,
    }

    if (token) {
      startCallUserWaitReporter.end({ callType, roomId, roomType })
      CallWappPanel.openCall(callType, token, {
        ...sharedInput,
        channelId: roomId,
        contentUri,
        watermarkEnabled:
          callType === 'classroom' && !tags?.includes(DISABLE_WATERMARK_TAG),
      })
    } else {
      startCallUserWaitReporter.clear()
      return // TODO: Show Error snackbar
    }

    callStorage.reset()
    callStorage.set({
      roomId,
      callType,
      joinedAt: new Date().getTime(),
      hallMeta: {
        name: callName,
        isOwner: true,
        isParticipating: true,
      },
    })
  } else {
    startCallUserWaitReporter.end({ callType, roomId, roomType })
    // handling Notification for call in PV
    twaCommunication.showCallNotification(
      roomId,
      TWA_EVENTS.SHOW_ONGOING_CALL_NOTIFICATION,
    )
  }
}

const joinCallUserWaitReporter = createUserWaitReporter(
  UserWaitPendingAction.JOIN_CALL,
)

async function getJoinRoomCredentials(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
) {
  const { error, payload } = await request<JoinCall>(
    reqJoinCall({
      roomId,
      roomType,
      callType,
    }),
  )

  if (error) {
    joinCallUserWaitReporter.clear()
    showErrorSnackbar(t('snackbar.call.joinFailed')(t(`call.${callType}`)))
    return false
  }

  const { error: resError, token, contentUri, serverAddress } = payload
  if (resError) {
    if (resError === 'USER_BANNED')
      showErrorSnackbar(t('snackbar.call.userBanned')(t(`call.${callType}`)))

    if (resError === 'ROOM_FULL') {
      showCallRoomIsFullDialog(callType)
      return false
    }
    joinCallUserWaitReporter.clear()
    return false
  }

  return {
    token: token!,
    contentUri,
    serverAddress: serverAddress || callServersConfig.mediaServerApiUrl,
  }
}

async function joinCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
) {
  if (callType === 'call') return
  if (!(await ensureHallCallAccess(roomId, roomType))) return

  const { roomId: ongoingCallRoomId } = callStorage.get()
  if (ongoingCallRoomId) await endOngoingCallWithConfirmation(roomId, callType)

  joinCallUserWaitReporter.start()

  const res = await getJoinRoomCredentials(roomId, roomType, callType)

  if (!res) return

  const { token, contentUri, serverAddress } = res

  const { name: hallName, tags } = await getHall(roomId)
  const { call } = (await getRoom(roomId)) || {}
  const callName = call?.name || hallName

  if (token) {
    joinCallUserWaitReporter.end({ roomId, roomType, callType })
    CallWappPanel.openCall(callType, token, {
      title: callName,
      startedAt: call?.startedAt,
      channelId: roomId,
      contentUri,
      watermarkEnabled:
        callType === 'classroom' && !tags?.includes(DISABLE_WATERMARK_TAG),
      roomType,
      callType,
      ...callServersConfig,
      mediaServerApiUrl: serverAddress,
    })
  } else {
    joinCallUserWaitReporter.clear()
    return // TODO: Show Error snackbar
  }

  callStorage.reset()
  callStorage.set({
    roomId,
    callType,
    startedAt: call?.startedAt,
    joinedAt: new Date().getTime(),
    hallMeta: { name: callName, isOwner: false, isParticipating: true },
  })

  twaCommunication.showCallNotification(
    roomId,
    TWA_EVENTS.SHOW_ONGOING_CALL_NOTIFICATION,
    callType,
  )
}

export async function joinCallFromRouter(
  roomId: string,
  callType: CallType,
  title?: string,
  input?: Record<string, any>,
) {
  if (!(await ensureHallCallAccess(roomId, 'CHANNEL'))) return

  const { payload } = await request<JoinCall>(
    reqJoinCall({
      roomId,
      roomType: 'CHANNEL',
      callType,
    }),
  )
  const { token, contentUri, serverAddress } = payload || {}

  if (token) {
    CallWappPanel.openCall(callType, token, {
      title,
      contentUri,
      ...callServersConfig,
      ...input,
      channelId: roomId,
      watermarkEnabled: false,
      mediaServerApiUrl: serverAddress || callServersConfig.mediaServerApiUrl,
      roomType: 'CHANNEL',
      callType,
    })

    callStorage.reset()
    callStorage.set({
      roomId,
      callType,
      startedAt: new Date().getTime(),
      joinedAt: new Date().getTime(),
      hallMeta: { name: callType, isOwner: false, isParticipating: true },
    })
  }
}

async function editCallName(name: string) {
  const { roomId, callType } = callStorage.get()
  if (!roomId || !callType) return

  const { type: roomType } = (await getRoom(roomId)) || {}
  if (!roomType) return

  const { error } = await request<UpdateCall>(
    reqUpdateCall({ roomId, roomType, callType, update: { name } }),
  )

  // TODO: handle error
  if (error) throw new Error()
  else callStorage.setHallMeta({ name })
}

async function sendNewNameToWapp(name: string) {
  if (!name) return

  callStorage.setHallMeta({ name })
  const wappMeta = CallWappPanel.getWappMeta()

  if (wappMeta.wappSessionId)
    sendEventToWapp(eventCallNameChanged({ name }, wappMeta))
}

async function cancelOutgoingCall() {
  const { roomId } = callStorage.getPvMeta()
  const { error } = await request<CancelCall>(
    reqCancelCall({
      roomId,
      roomType: 'PV',
      callType: 'call',
    }),
  )

  if (error) {
    // TODO: handle error
  } else {
    callStorage.resetPvMeta()
    closePvCallManager()
  }
}

async function acceptIncomingCall() {
  const { roomId: ongoingCallRoomId } = callStorage.get()

  const pvMeta = callStorage.getPvMeta()
  const { roomId: incomingCallRoomId } = pvMeta

  if (ongoingCallRoomId)
    await endOngoingCallWithConfirmation(incomingCallRoomId, 'call')
  joinCallUserWaitReporter.start()
  callStorage.setPvMeta({ acceptingIncomingCall: true })
  const { error } = await request<JoinCall>(
    reqJoinCall({
      roomId: incomingCallRoomId,
      roomType: 'PV',
      callType: 'call',
    }),
  )

  if (error) {
    // TODO: handle error
    joinCallUserWaitReporter.clear()
    callStorage.resetPvMeta()
    closePvCallManager()
  } else {
    callStorage.reset()
    callStorage.set({
      roomId: incomingCallRoomId,
      callType: 'call',
      pvMeta: {
        ...pvMeta,
        roomId: incomingCallRoomId,
      },
    })

    joinCallUserWaitReporter.end({
      callType: 'call',
      roomId: incomingCallRoomId,
      roomType: 'PV',
    })
    twaCommunication.showCallNotification(
      incomingCallRoomId,
      TWA_EVENTS.SHOW_ONGOING_CALL_NOTIFICATION,
    )
  }
}

const rejectCallUserWaitReporter = createUserWaitReporter(
  UserWaitPendingAction.REJECT_INCOMING_CALL,
)
async function rejectIncomingCall() {
  sendEventToTwa(TWA_EVENTS.REJECT_INCOMING_CALL)

  const { roomId } = callStorage.getPvMeta()

  rejectCallUserWaitReporter.start()
  const { error } = await request<RejectCall>(
    reqRejectCall({
      roomId,
      roomType: 'PV',
      callType: 'call',
    }),
  )

  if (error) {
    // TODO: handle error
    rejectCallUserWaitReporter.clear()
  } else {
    rejectCallUserWaitReporter.end({
      roomId,
      roomType: 'PV',
      callType: 'call',
    })
    callStorage.resetPvMeta()
    closePvCallManager()
  }
}

const NPSShownCalls = new Set<string>()

async function getFeedbackWithScore(
  call: Pick<CallStorage, 'callType' | 'roomId'>,
  identifier?: string,
) {
  if (!call?.callType) return
  const feedback = await showCallFeedbackDialog(call.callType)

  analite('SUBMIT_CALL_SCORE', {
    section: call.callType,
    roomId: call.roomId,
    identifier,
    score: feedback.rating,
    feedback: feedback.problem,
    feedbackDetails: feedback.problemDescription,
  })
}

/**
 *
 * @param call should be a deepClone of CallStorage
 * @returns
 */
export async function showCallNPS(call: CallStorage) {
  if (!call.roomId || !call.callType) {
    return
  }

  const callId = getCallId(call)
  if (!callId || NPSShownCalls.has(callId) || !isCallAtLeastOneMinuteLong(call))
    return

  const isPublic = await isPublicHall(call.roomId)
  const identifier = isPublic ? call.roomId : undefined

  await getFeedbackWithScore(call, identifier)
  NPSShownCalls.add(callId)
}

export async function showCallNPSByWapp(call: {
  callType: CallType
  roomId: string
  startedAt: number
}) {
  const { roomId, callType } = call
  if (!roomId || !callType) return

  const callId = getCallId(call)
  if (
    !callId ||
    NPSShownCalls.has(callId) ||
    // startedAt in call wapp is the time that user joined that wapp.
    !isCallAtLeastOneMinuteLong({ joinedAt: call.startedAt })
  )
    return

  const isPublic = await isPublicHall(call.roomId)
  const identifier = isPublic ? call.roomId : undefined

  await getFeedbackWithScore(call, identifier)
  NPSShownCalls.add(callId)
}

async function endOngoingHallCall(callType: CallType) {
  sendEventToTwa(TWA_EVENTS.END_ONGOING_CALL)

  const call = callStorage.get()
  if (call.hasNextCall) return

  if (callType === 'group_call') await CallWappPanel.closeCall(false)

  if (callType !== 'classroom') {
    const clonedCall = cloneDeep(call)
    showCallNPS(clonedCall)
  }

  callStorage.reset()
}

async function endOngoingPvCall(
  duration?: number,
  status?: CallStoragePvMeta['status'],
) {
  sendEventToTwa(TWA_EVENTS.END_ONGOING_CALL)

  const call = callStorage.get()
  const clonedCall = cloneDeep(call)

  if (call.hasNextCall) return

  const { callType: ongoingCallType } = call
  if (ongoingCallType === 'call') {
    await CallWappPanel.closeCall(false)
    callStorage.reset()
  }

  const { roomId: pvMetaRoomId, isCaller: wasCaller } = callStorage.getPvMeta()

  if (
    status === 'canceled' ||
    (!wasCaller && (status === 'rejected' || status === 'timed-out'))
  ) {
    callStorage.resetPvMeta()
    closePvCallManager()
  } else {
    callStorage.setPvMeta({
      roomId: clonedCall.roomId || pvMetaRoomId,
      duration,
      status,
      state: 'ended',
    })

    openPvCallManager()
  }

  if (clonedCall.callType === 'call') showCallNPS(clonedCall)
}

async function checkForIncomingCalls() {
  const clientUserId = getClientUserId()
  const ringingPvs =
    getRooms().filter(
      ({ type, call }) =>
        type === 'PV' &&
        call?.type === 'call' &&
        clientUserId !== call.callerId &&
        !call.startedAt,
    ) || []

  const [ringingRoom] = ringingPvs
  if (ringingRoom) {
    callStorage.setPvMeta({
      roomId: ringingRoom.id,
      state: 'incoming',
      isCaller: false,
    })

    openPvCallManager()
  }
}

async function acceptIncomingCallByRoomId(roomId: string) {
  const { roomId: callRoomId } = callStorage.getPvMeta() || {}
  if (callRoomId && callRoomId === roomId) await acceptIncomingCall()
}

async function endOngoingCallByRoomId(roomId: string) {
  const { roomId: callRoomId, callType } = callStorage.get() || {}
  if (!callType || !callRoomId || callRoomId !== roomId) return

  if (callType === 'call') await endOngoingPvCall()
  else await endOngoingHallCall(callType)
}

async function rejectIncomingCallByRoomId(roomId: string) {
  const { roomId: callRoomId } = callStorage.getPvMeta() || {}
  if (callRoomId && callRoomId === roomId) resetCallAndShowNPS()
}

async function getCallAttendanceReport(uuid: string) {
  const authToken = await getClientUserIdentityToken()

  const url = `${hostConf.MEDIA_PROXY_SERVER}/report?uuid=${uuid}&token=${authToken}`
  downloadFileFromUrl(url, 'AttendanceList.xlsx')
}

export const callService = {
  startCall,
  joinCall,
  joinCallFromRouter,
  endOngoingHallCall,
  endOngoingPvCall,
  editCallName,
  sendNewNameToWapp,
  cancelOutgoingCall,
  acceptIncomingCall,
  rejectIncomingCall,
  checkForIncomingCalls,
  scheduleCall,
  unscheduleCall,
  updateScheduledCall,
  acceptIncomingCallByRoomId,
  endOngoingCallByRoomId,
  rejectIncomingCallByRoomId,
  getCallAttendanceReport,
  getJoinRoomCredentials,
}
